import { useEffect, useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Clock, MapPin, MessageCircle, Images, X, Lock, Loader2, Minus, Plus, Waves, ShieldCheck } from "lucide-react";
import { mediaUrl } from "@/lib/pricing";
import { waLink } from "@/lib/site";
import { PhotoLightbox } from "./PhotoLightbox";
import { PixPayment, BRL } from "./PixPayment";
import { GuestFields, TermsCheckbox, EMPTY_GUEST, guestError, guestPayload } from "./GuestFields";
import { CouponField, couponDiscount } from "./CouponField";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const todayISO = () => new Date().toLocaleDateString("sv-SE");
const fmtBR = (s) => s.split("-").reverse().join("/");
export const durationLabel = (m) => (m >= 60 ? `${Math.floor(m / 60)}h${m % 60 ? ` ${m % 60}min` : ""}` : `${m} min`);

export const Stepper = ({ value, onChange, max, testId }) => (
  <div className="flex items-center gap-2" data-testid={testId}>
    <button type="button" onClick={() => onChange(Math.max(1, value - 1))} className="h-8 w-8 rounded-full border border-slate-200 flex items-center justify-center" aria-label="Menos"><Minus size={14} /></button>
    <span className="w-6 text-center font-bold">{value}</span>
    <button type="button" onClick={() => onChange(Math.min(max, value + 1))} className="h-8 w-8 rounded-full border border-slate-200 flex items-center justify-center" aria-label="Mais"><Plus size={14} /></button>
  </div>
);

export function TourModal({ tour, onClose }) {
  const [date, setDate] = useState(todayISO());
  const [time, setTime] = useState(tour.time_slots?.[0] || "");
  const [qty, setQty] = useState(1);
  const [form, setForm] = useState(EMPTY_GUEST);
  const [accepted, setAccepted] = useState(false);
  const [stage, setStage] = useState("form");
  const [booking, setBooking] = useState(null);
  const [sending, setSending] = useState(false);
  const [coupon, setCoupon] = useState(null);

  useEffect(() => {
    const onKey = (e) => e.key === "Escape" && onClose();
    document.addEventListener("keydown", onKey);
    document.body.style.overflow = "hidden";
    return () => { document.removeEventListener("keydown", onKey); document.body.style.overflow = ""; };
  }, [onClose]);

  const gross = Number(tour.price) * qty;
  const discount = coupon && ["all", "tour"].includes(coupon.applies_to || "all") ? couponDiscount(coupon, gross) : 0;
  const total = gross - discount;
  const deposit = (total * Number(tour.deposit_percent ?? 50)) / 100;

  const submit = async () => {
    const err = !date || !time ? "Escolha a data e o horário" : guestError(form, accepted);
    if (err) return toast.error(err);
    setSending(true);
    try {
      const r = await axios.post(`${API}/public/tour-bookings`, { ...guestPayload(form), item: { tour_id: tour.id, date, time, qty }, coupon_code: coupon?.code || null });
      setBooking(r.data);
      setStage("pix");
    } catch (e) {
      const d = e.response?.data?.detail;
      toast.error(typeof d === "string" ? d : "Não foi possível gerar o PIX. Confira os dados e tente novamente.");
    }
    setSending(false);
  };

  return (
    <div className="fixed inset-0 z-[100] flex p-4 bg-slate-900/70 backdrop-blur-md overflow-y-auto" onClick={onClose} data-testid="tour-modal">
      <div className="bg-white w-full max-w-md rounded-[2rem] shadow-2xl overflow-hidden m-auto shrink-0" onClick={(e) => e.stopPropagation()}>
        <div className="bg-[#0b3b66] p-6 text-white flex justify-between items-start gap-4">
          <div>
            <p className="text-cyan-200 text-[11px] font-bold uppercase mb-1">{stage === "form" ? "Reservar passeio" : stage === "confirmed" ? "Passeio confirmado" : "Pagamento via PIX"}</p>
            <h3 className="text-xl font-extrabold leading-tight">{tour.title}</h3>
            <p className="text-blue-100 text-sm mt-1"><MapPin className="w-3 h-3 inline mr-1" />{tour.location}</p>
          </div>
          <button onClick={onClose} aria-label="Fechar" className="bg-white/20 p-2 rounded-full hover:bg-white/30 shrink-0" data-testid="tour-modal-close"><X className="w-5 h-5" /></button>
        </div>
        <div className="p-6">
          {stage === "form" ? (
            <>
              <div className="grid grid-cols-2 gap-3 mb-3">
                <div>
                  <label className="text-[11px] font-bold text-slate-500 uppercase mb-1.5 block">Data</label>
                  <input type="date" min={todayISO()} value={date} onChange={(e) => setDate(e.target.value)} className="w-full p-2.5 rounded-xl border border-slate-200 text-sm" data-testid="tour-date" />
                </div>
                <div>
                  <label className="text-[11px] font-bold text-slate-500 uppercase mb-1.5 block">Jet skis</label>
                  <Stepper value={qty} onChange={setQty} max={tour.units || 1} testId="tour-qty" />
                </div>
              </div>
              <label className="text-[11px] font-bold text-slate-500 uppercase mb-1.5 block">Horário</label>
              <div className="flex flex-wrap gap-2 mb-4">
                {tour.time_slots.map((t) => (
                  <button key={t} type="button" onClick={() => setTime(t)} data-testid={`tour-time-${t}`}
                    className={`px-3.5 py-2 rounded-full text-sm font-bold border transition-colors ${time === t ? "bg-[#0A66C2] border-[#0A66C2] text-white" : "border-slate-200 text-slate-600 hover:border-[#0A66C2]"}`}>{t}</button>
                ))}
              </div>
              <GuestFields form={form} setForm={setForm} />
              <div className="bg-cyan-50 p-4 rounded-2xl mb-4 border border-cyan-100 text-sm">
                <div className="flex justify-between mb-1"><span className="text-slate-500">{qty} × {BRL(tour.price)} ({tour.price_unit})</span><span className="font-bold">{BRL(gross)}</span></div>
                {discount > 0 && <div className="flex justify-between mb-1 text-emerald-700" data-testid="tour-coupon-discount"><span>Cupom {coupon.code}</span><span className="font-bold">−{BRL(discount)}</span></div>}
                <div className="flex justify-between mb-1"><span className="text-slate-500">Total</span><span className="font-bold" data-testid="tour-total">{BRL(total)}</span></div>
                <div className="flex justify-between items-end pt-2 border-t border-cyan-200">
                  <span className="text-[#0A66C2] text-[11px] font-bold uppercase">Sinal agora via PIX</span>
                  <span className="text-xl font-extrabold text-[#0A66C2]" data-testid="tour-deposit">{BRL(deposit)}</span>
                </div>
                <div className="flex justify-between text-xs text-slate-500 mt-1"><span>Restante, pago no dia do passeio</span><span>{BRL(total - deposit)}</span></div>
              </div>
              <CouponField kind="tour" total={gross} coupon={coupon} setCoupon={setCoupon} />
              <TermsCheckbox accepted={accepted} setAccepted={setAccepted} />
              <button onClick={submit} disabled={sending} data-testid="tour-submit"
                className="w-full bg-[#FFCC00] text-blue-900 py-4 rounded-2xl font-extrabold text-lg shadow-[0_4px_0_#b38f00] active:translate-y-1 active:shadow-none transition-transform flex items-center justify-center gap-3 disabled:opacity-60">
                {sending ? <><Loader2 className="w-5 h-5 animate-spin" /> Gerando PIX...</> : <>Pagar sinal com PIX <Lock className="w-5 h-5" /></>}
              </button>
              <p className="text-center text-xs text-slate-400 mt-4 flex items-center justify-center gap-2"><ShieldCheck className="w-4 h-4" /> Pagamento processado pela LofyPay · conexão segura</p>
            </>
          ) : (
            <PixPayment stage={stage} booking={booking} onStage={setStage} onClose={onClose}
              onRetry={() => { setBooking(null); setStage("form"); }}
              confirmedText="Seu passeio está garantido. Em breve o instrutor entrará em contato pelo WhatsApp com o ponto de encontro."
              rows={[["Passeio", tour.title], ["Data", `${fmtBR(date)} às ${time}`], ["Jet skis", qty], ["Sinal pago", BRL(booking?.deposit_amount)], ["Restante no dia", BRL((booking?.total_amount || 0) - (booking?.deposit_amount || 0))]]} />
          )}
        </div>
      </div>
    </div>
  );
}

function TourCard({ t, onBook }) {
  const [gallery, setGallery] = useState(false);
  const wa = waLink(t.whatsapp, `Olá, equipe Se Hospede AKI! Tenho interesse no passeio de jet ski "${t.title}" e gostaria de tirar algumas dúvidas antes de reservar:\n\n• Horários disponíveis na minha data\n• Quantas pessoas podem ir por jet ski\n• Ponto de encontro e o que está incluso\n\nAguardo o retorno. Obrigado(a)!`);
  return (
    <article className="bg-white rounded-[2rem] overflow-hidden border border-slate-200 shadow-sm hover:shadow-xl transition-shadow flex flex-col" data-testid={`tour-card-${t.id}`}>
      {gallery && <PhotoLightbox images={t.images} title={t.title} onClose={() => setGallery(false)} />}
      <div className="relative h-56 bg-slate-100 cursor-zoom-in" onClick={() => t.images?.length && setGallery(true)}>
        {t.images?.[0] && <img src={mediaUrl(t.images[0])} alt={t.title} className="w-full h-full object-cover" loading="lazy" />}
        <span className="absolute top-4 left-4 bg-cyan-500 text-white text-xs font-bold uppercase px-3 py-1 rounded-full flex items-center gap-1"><Waves size={13} /> Jet Ski</span>
        {t.images?.length > 0 && (
          <button onClick={(e) => { e.stopPropagation(); setGallery(true); }} data-testid={`tour-gallery-${t.id}`}
            className="absolute bottom-3 right-3 bg-slate-900/75 text-white text-xs font-bold px-3 py-1.5 rounded-full flex items-center gap-1.5">
            <Images size={14} /> {t.images.length === 1 ? "Ver foto" : `Ver ${t.images.length} fotos`}
          </button>
        )}
      </div>
      <div className="p-6 flex flex-col flex-1">
        <h3 className="text-xl font-bold text-slate-800 leading-tight mb-1"><a href={`/passeio/${t.id}`} className="hover:text-[#0A66C2]" data-testid={`tour-link-${t.id}`}>{t.title}</a></h3>
        <p className="text-slate-500 text-sm flex items-center gap-1.5 mb-3"><MapPin className="w-4 h-4 text-[#0A66C2]" /> {t.location}</p>
        <div className="flex flex-wrap gap-3 text-xs text-slate-500 mb-3">
          <span className="flex items-center gap-1"><Clock className="w-4 h-4" /> {durationLabel(t.duration_min)}</span>
          <span>até {t.max_people_per_unit} {t.max_people_per_unit === 1 ? "pessoa" : "pessoas"} por jet ski</span>
        </div>
        {t.description && <p className="text-sm text-slate-500 mb-2 line-clamp-3">{t.description}</p>}
        <a href={`/passeio/${t.id}`} className="text-sm font-bold text-[#0A66C2] mb-4 hover:underline" data-testid={`tour-details-${t.id}`}>Ver detalhes e fotos →</a>
        <div className="mt-auto flex items-end justify-between gap-3 pt-4 border-t border-slate-100">
          <div>
            <span className="text-slate-400 text-[11px] block font-bold uppercase">{t.price_unit}</span>
            <span className="text-2xl font-extrabold text-[#0A66C2]">{BRL(t.price)}</span>
          </div>
          <div className="flex gap-2">
            {wa && <a href={wa} target="_blank" rel="noreferrer" aria-label="Tirar dúvidas no WhatsApp" className="h-12 w-12 rounded-2xl bg-[#25D366] text-white flex items-center justify-center" data-testid={`tour-whatsapp-${t.id}`}><MessageCircle size={20} /></a>}
            <button onClick={onBook} className="bg-[#0A66C2] text-white px-5 py-3 rounded-2xl font-bold hover:bg-blue-700 transition-colors" data-testid={`tour-book-${t.id}`}>Reservar</button>
          </div>
        </div>
      </div>
    </article>
  );
}

export const useTours = () => {
  const [tours, setTours] = useState([]);
  useEffect(() => { axios.get(`${API}/public/tours`).then((r) => setTours(r.data || [])).catch(() => {}); }, []);
  return tours;
};

export const ToursSection = ({ tours }) => {
  const [selected, setSelected] = useState(null);
  if (!tours.length) return null;
  return (
    <section id="passeios" className="py-20 bg-gradient-to-b from-cyan-50 to-white scroll-mt-20" data-testid="tours-section">
      <div className="max-w-7xl mx-auto px-4">
        <div className="text-center mb-14">
          <span className="inline-flex items-center gap-2 bg-cyan-100 text-cyan-800 px-4 py-1.5 rounded-full text-xs font-bold uppercase mb-4"><Waves className="w-3 h-3" /> Passeios</span>
          <h2 className="text-3xl sm:text-4xl md:text-5xl font-extrabold mb-4 text-slate-900">Jet Ski no litoral de São Paulo</h2>
          <p className="text-slate-500 text-base sm:text-lg max-w-2xl mx-auto">Passeios com instrutor no Guarujá e no Litoral Norte. Reserve sozinho ou junto com a sua hospedagem.</p>
        </div>
        <div className={`grid grid-cols-1 md:grid-cols-2 gap-8 ${tours.length >= 3 ? "lg:grid-cols-3" : "max-w-5xl mx-auto"}`}>
          {tours.map((t) => <TourCard key={t.id} t={t} onBook={() => setSelected(t)} />)}
        </div>
      </div>
      {selected && <TourModal tour={selected} onClose={() => setSelected(null)} />}
    </section>
  );
};
